import React, { useEffect } from 'react';
import { AlertTriangle, Check, ChevronRight, Info, LoaderCircle, X } from 'lucide-react';
import { classNames, confidenceLabel, initials, scoreTone, statusLabel } from '../utils.js';

export function Button({ children, icon: Icon, variant = 'secondary', size = 'md', className, loading, ...props }) {
  return (
    <button className={classNames('button', `button-${variant}`, `button-${size}`, className)} disabled={loading || props.disabled} {...props}>
      {loading ? <LoaderCircle size={16} className="spin" /> : Icon ? <Icon size={16} strokeWidth={2} /> : null}
      {children && <span>{children}</span>}
    </button>
  );
}

export function IconButton({ icon: Icon, label, variant = 'ghost', className, ...props }) {
  return (
    <button className={classNames('icon-button', `button-${variant}`, className)} title={label} aria-label={label} {...props}>
      <Icon size={18} strokeWidth={2} />
    </button>
  );
}

export function Badge({ children, tone = 'neutral', dot = false, className, ...props }) {
  return <span className={classNames('badge', `badge-${tone}`, className)} {...props}>{dot && <i />}{children}</span>;
}

export function StatusBadge({ status }) {
  const tone = status === 'scored' || status === 'completed' || status === 'active'
    ? 'success' : status === 'needs_review' || status === 'queued' || status === 'ready' ? 'warning'
      : status === 'processing' ? 'info' : status === 'failed' ? 'danger' : 'neutral';
  return <Badge tone={tone} dot>{statusLabel(status)}</Badge>;
}

export function ScoreRing({ score, size = 52, label, className }) {
  const safe = score === null || score === undefined ? 0 : Math.max(0, Math.min(100, Number(score)));
  return (
    <div
      className={classNames('score-ring', `score-${scoreTone(score)}`, className)}
      style={{ '--score': safe, '--ring-size': `${size}px` }}
      title={label || (score === null ? '尚未评分' : `${score} 分`)}
    >
      <div><strong>{score === null || score === undefined ? '—' : Math.round(score)}</strong>{size >= 64 && <small>/100</small>}</div>
    </div>
  );
}

export function Confidence({ value, compact = false }) {
  const safe = Math.round((Number(value) || 0) * 100);
  return (
    <div className={classNames('confidence', compact && 'confidence-compact')} title={`AI 置信度 ${safe}%`}>
      {!compact && <span>置信度 {confidenceLabel(value)}</span>}
      <div className="confidence-track"><i style={{ width: `${safe}%` }} /></div>
      <strong>{safe}%</strong>
    </div>
  );
}

export function Avatar({ name, avatarUrl, src, size = 'md', tone = 0, className }) {
  const [imageError, setImageError] = React.useState(false);
  const effectiveSrc = src || avatarUrl;
  return (
    <span className={classNames('avatar', `avatar-${size}`, `avatar-tone-${tone % 5}`, className)} aria-hidden="true">
      {effectiveSrc && !imageError ? (
        <img
          src={effectiveSrc}
          alt={name || ''}
          className="avatar-img"
          onError={() => setImageError(true)}
        />
      ) : (
        <span>{initials(name)}</span>
      )}
    </span>
  );
}

export function PageHeader({ eyebrow, title, description, actions }) {
  return (
    <header className="page-header">
      <div>
        {eyebrow && <div className="page-eyebrow">{eyebrow}</div>}
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}

export function SectionHeader({ title, description, action }) {
  return (
    <div className="section-header">
      <div><h2>{title}</h2>{description && <p>{description}</p>}</div>
      {action}
    </div>
  );
}

export function EmptyState({ icon: Icon = Info, title, description, action }) {
  return (
    <div className="empty-state">
      <span className="empty-icon"><Icon size={22} /></span>
      <h3>{title}</h3>
      {description && <p>{description}</p>}
      {action}
    </div>
  );
}

export function Modal({ open, title, description, children, footer, onClose, width = '560px' }) {
  useEffect(() => {
    if (!open) return undefined;
    const handler = (event) => event.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose?.()}>
      <section className="modal" role="dialog" aria-modal="true" style={{ maxWidth: width }}>
        <header>
          <div><h2>{title}</h2>{description && <p>{description}</p>}</div>
          <IconButton icon={X} label="关闭" onClick={onClose} />
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer>{footer}</footer>}
      </section>
    </div>
  );
}

export function Callout({ tone = 'info', title, children, action }) {
  const Icon = tone === 'danger' || tone === 'warning' ? AlertTriangle : tone === 'success' ? Check : Info;
  return (
    <div className={classNames('callout', `callout-${tone}`)}>
      <Icon size={18} />
      <div><strong>{title}</strong>{children && <p>{children}</p>}</div>
      {action}
    </div>
  );
}

export function InlineLink({ children, onClick }) {
  return <button className="inline-link" onClick={onClick}>{children}<ChevronRight size={14} /></button>;
}

export function ProgressBar({ value, tone = 'green', label }) {
  return (
    <div className="progress-block">
      {label && <div className="progress-label"><span>{label}</span><strong>{Math.round(value)}%</strong></div>}
      <div className={classNames('progress-bar', `progress-${tone}`)}><i style={{ width: `${Math.max(0, Math.min(100, value))}%` }} /></div>
    </div>
  );
}

export function Skeleton({ rows = 4 }) {
  return <div className="skeleton-list">{Array.from({ length: rows }, (_, index) => <i key={index} style={{ width: `${95 - index * 8}%` }} />)}</div>;
}
